// What the home page recommends (Oct 2026): Top picks, then a More like shelf
// for each of the tunes you opened last, worked out together so no tune turns
// up twice on the page. Everything comes from the precomputed library file
// and the store; the scoring is src/music/recommend.ts.
//
// Love (state/tuneTaste.ts) counts from the page's next visit: the hook
// reads the marks once, when it mounts, so a heart tapped on a Top pick does
// not reshuffle the cards under your finger. Marks the account sync brings
// from your other devices count at once (`tastePulls`): nobody tapped them
// here. Your playlists are read the same way, once: a tune in a playlist is
// one of yours, so a Top pick a card adds to a playlist (TileAddToPlaylist)
// would otherwise vanish as you added it. Recents and play history are your
// devices' together (historyView).

import { useMemo } from 'react';
import { libraryTuneMatcher } from '@/music/libraryIdentity';
import type { LibraryStats, LibraryStatsTune } from '@/music/libraryStats';
import { isoWeek, moreLike, topPicks, type MoreLikeItem, type TopPick } from '@/music/recommend';
import { useHistoryView, useRecentsView } from '@/state/historyView';
import { useStore } from '@/state/store';
import { mostPlayedShelf } from '@/state/tuneHistory';
import { lovedTunes, type TasteTune } from '@/state/tuneTaste';
import { useLibraryStats } from './libraryStats';
import { usePlaylistsAvailable } from './playlistAccess';
import { tasteHistory, type TasteInputs } from './tasteHistory';

export interface MoreLikeShelf {
  /** The tune the shelf is like, by index into the library's tunes. */
  anchor: number;
  items: MoreLikeItem[];
}

export interface HomeRecommendations {
  picks: TopPick[];
  shelves: MoreLikeShelf[];
}

/** Top picks shown. */
export const PICKS = 6;
/** More like shelves at most, and the fewest tunes worth a shelf. */
export const SHELVES = 3;
export const SHELF_MIN = 4;

/**
 * Top picks and the More like shelves. A shelf is like one of your recent
 * tunes, newest first; it leaves out every recent tune and every tune Most
 * played or Loved shows (those shelves show them), the picks and
 * the shelves above it, and a tune with too little like it gets no shelf, so
 * the next recent one does.
 */
export function homeRecommendations(
  stats: Pick<LibraryStats, 'tunes' | 'neighbours' | 'feels'>,
  taste: TasteInputs,
  week: number,
): HomeRecommendations {
  const find = libraryTuneMatcher(stats.tunes.map((tune) => ({ title: tune.t, composer: tune.c })));
  const picks = topPicks({
    tunes: stats.tunes,
    neighbours: stats.neighbours,
    history: tasteHistory(taste, find),
    week,
    count: PICKS,
  });

  const recent: number[] = [];
  for (const ref of taste.recentTunes) {
    const tune = find(ref.title, ref.composer);
    if (tune !== null && !recent.includes(tune)) recent.push(tune);
  }
  const feel = new Map<number, string>();
  for (const group of stats.feels) for (const tune of group.tunes) feel.set(tune, group.label);
  const shown = new Set([...recent, ...picks.map((pick) => pick.tune)]);
  // Most played is on the page too, above the shelves.
  for (const entry of mostPlayedShelf(taste.tuneHistory ?? {})) {
    const tune = find(entry.title, entry.composer);
    if (tune !== null) shown.add(tune);
  }
  for (const mark of lovedTunes(taste.tuneTaste ?? [])) {
    const tune = find(mark.title, mark.composer);
    if (tune !== null) shown.add(tune);
  }

  const shelves: MoreLikeShelf[] = [];
  for (const anchor of recent) {
    if (shelves.length === SHELVES) break;
    const items = moreLike(anchor, {
      tunes: stats.tunes,
      neighbours: stats.neighbours,
      feelOf: (tune) => feel.get(tune),
      exclude: shown,
    });
    if (items.length < SHELF_MIN) continue;
    shelves.push({ anchor, items });
    for (const item of items) shown.add(item.tune);
  }
  return { picks, shelves };
}

/** A library tune as Love marks it. */
export function libraryTasteTune(tune: LibraryStatsTune): TasteTune {
  return {
    title: tune.t,
    ...(tune.c ? { composer: tune.c } : {}),
    ...(tune.rb ? { source: 'realbook' as const } : {}),
  };
}

/** The home page's recommendations, or null until the library file is in.
 * Love, and your playlists, as they stood when the page
 * opened. */
export function useHomeRecommendations(): (HomeRecommendations & { stats: LibraryStats }) | null {
  const stats = useLibraryStats();
  const recentTunes = useRecentsView();
  const savedCharts = useStore((s) => s.savedCharts);
  const tuneHistory = useHistoryView();
  // The marks as the page opened, and again only when the sync brings some.
  const tastePulls = useStore((s) => s.tastePulls);
  const tuneTaste = useMemo(
    () => useStore.getState().tuneTaste,
    [tastePulls], // eslint-disable-line react-hooks/exhaustive-deps -- re-read only when the sync pulls
  );
  // Your playlists as the page opened, and again when playlists become yours
  // to have (signing in).
  const available = usePlaylistsAvailable();
  const playlists = useMemo(
    () => (available ? useStore.getState().userPlaylists : []),
    [available],
  );
  return useMemo(() => {
    if (!stats) return null;
    const now = new Date();
    return {
      stats,
      ...homeRecommendations(
        stats,
        { recentTunes, savedCharts, playlists, tuneHistory, tuneTaste, now: now.getTime() },
        isoWeek(now),
      ),
    };
  }, [stats, recentTunes, savedCharts, playlists, tuneHistory, tuneTaste]);
}
